import type {
  CasoBenchessmark,
  DesafioSala,
  EstadoMotorPublico,
  LinhaAnalisePublica,
  ModeloPublico,
  ModoAssistenciaModelo,
  MotorPublico,
  NivelClassico,
  PartidaOnlineEstado,
  PartidaPublicaResumo,
  PresencaSala,
  ProvedorAdministrativo,
  ProvedorEditavel,
  ResultadoItemBenchessmark
} from '../types';
import { tokenAdmin } from './adminAuth';

async function interpretar<T>(resposta: Response): Promise<T> {
  const dados = (await resposta.json().catch(() => ({ mensagem: 'A resposta do servidor não pôde ser lida.' }))) as T & {
    mensagem?: string;
  };
  if (!resposta.ok) throw new Error(dados.mensagem || 'Não foi possível concluir a operação.');
  return dados;
}

function cabecalhosAdmin(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = tokenAdmin();
  if (token) headers['x-token-administrativo'] = token;
  return headers;
}

export async function listarModelosPublicos(): Promise<ModeloPublico[]> {
  const resposta = await fetch('/api/jogo/modelos');
  const dados = await interpretar<{ modelos: ModeloPublico[] }>(resposta);
  return dados.modelos;
}

/** ——— Sala multiplayer ——— */
export async function salaEntrar(nome: string, clienteId?: string): Promise<{ cliente: PresencaSala; sala: PresencaSala[] }> {
  const resposta = await fetch('/api/sala/entrar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nome, clienteId })
  });
  return interpretar(resposta);
}

export async function salaPing(clienteId: string): Promise<{
  cliente: PresencaSala;
  sala: PresencaSala[];
  desafios: DesafioSala[];
  partida?: PartidaOnlineEstado;
  partidasPublicas?: PartidaPublicaResumo[];
}> {
  const resposta = await fetch('/api/sala/ping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clienteId })
  });
  return interpretar(resposta);
}

export async function salaSair(clienteId: string): Promise<void> {
  await fetch('/api/sala/sair', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clienteId })
  });
}

export async function salaDesafiar(
  deId: string,
  paraId: string,
  permitirEspectadores = false
): Promise<DesafioSala> {
  const resposta = await fetch('/api/sala/desafiar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deId, paraId, permitirEspectadores })
  });
  const dados = await interpretar<{ desafio: DesafioSala }>(resposta);
  return dados.desafio;
}

export async function salaResponder(
  clienteId: string,
  desafioId: string,
  aceitar: boolean,
  permitirEspectadores = false
): Promise<{ recusado?: boolean; partida?: PartidaOnlineEstado }> {
  const resposta = await fetch('/api/sala/responder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clienteId, desafioId, aceitar, permitirEspectadores })
  });
  return interpretar(resposta);
}

export async function salaObterPartida(id: string, clienteId?: string): Promise<PartidaOnlineEstado> {
  const q = clienteId ? `?clienteId=${encodeURIComponent(clienteId)}` : '';
  const resposta = await fetch(`/api/sala/partida/${id}${q}`);
  const dados = await interpretar<{ partida: PartidaOnlineEstado }>(resposta);
  return dados.partida;
}

export async function salaJogar(
  partidaId: string,
  clienteId: string,
  de: string,
  para: string,
  promocao?: string
): Promise<PartidaOnlineEstado> {
  const resposta = await fetch(`/api/sala/partida/${partidaId}/jogada`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clienteId, de, para, promocao })
  });
  const dados = await interpretar<{ partida: PartidaOnlineEstado }>(resposta);
  return dados.partida;
}

export async function salaEnviarChat(
  partidaId: string,
  clienteId: string,
  texto: string
): Promise<PartidaOnlineEstado> {
  const resposta = await fetch(`/api/sala/partida/${partidaId}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clienteId, texto })
  });
  const dados = await interpretar<{ partida: PartidaOnlineEstado }>(resposta);
  return dados.partida;
}

export async function salaAbandonar(partidaId: string, clienteId: string): Promise<PartidaOnlineEstado> {
  const resposta = await fetch(`/api/sala/partida/${partidaId}/abandonar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clienteId })
  });
  const dados = await interpretar<{ partida: PartidaOnlineEstado }>(resposta);
  return dados.partida;
}

export async function salaListarPartidasPublicas(): Promise<PartidaPublicaResumo[]> {
  const resposta = await fetch('/api/sala/partidas-publicas');
  const dados = await interpretar<{ partidas: PartidaPublicaResumo[] }>(resposta);
  return dados.partidas;
}

export async function listarMotores(): Promise<MotorPublico[]> {
  const resposta = await fetch('/api/jogo/motores');
  const dados = await interpretar<{ motores: MotorPublico[] }>(resposta);
  return dados.motores;
}

export interface PedidoJogadaModelo {
  modeloPublicoId: string;
  fen: string;
  pgn: string;
  cor: 'w' | 'b';
  jogadasLegais?: string[];
  jogadasSan?: string[];
  estilo: string;
  assistencia?: ModoAssistenciaModelo;
  tempoBrancas: number;
  tempoPretas: number;
  controle: string;
  idPartida: string;
  idPosicao: string;
}

export interface RespostaJogadaModelo {
  jogada: string;
  tentativas: number;
  usouAlternativa?: boolean;
  ajusteQualidade?: boolean;
  perdaPrevistaCp?: number;
  motivoAlternativa?: string;
  latenciaMs?: number;
  melhorStockfish?: string;
  topStockfish?: string[];
  assistencia?: ModoAssistenciaModelo;
  nomeMotor?: string;
}

export async function solicitarJogadaModelo(pedido: PedidoJogadaModelo, sinal: AbortSignal): Promise<RespostaJogadaModelo> {
  const resposta = await fetch('/api/jogo/modelo/jogada', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assistencia: 'regio', ...pedido }),
    signal: sinal
  });
  return interpretar<RespostaJogadaModelo>(resposta);
}

export interface RespostaJogadaMotor {
  jogada: string;
  motor?: string;
  nomeMotor?: string;
  origem?: string;
  duracaoMs?: number;
  profundidade?: number;
}

export async function solicitarJogadaMotor(
  fen: string,
  nivel: NivelClassico,
  opts: { motor?: 'stockfish' | 'lc0'; tempoDisponivelMs?: number; idPartida?: string; idPosicao?: string },
  sinal: AbortSignal
): Promise<RespostaJogadaMotor> {
  const resposta = await fetch('/api/motor/jogada', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fen,
      nivel,
      motor: opts.motor ?? 'stockfish',
      tempoDisponivelMs: opts.tempoDisponivelMs,
      idPartida: opts.idPartida,
      idPosicao: opts.idPosicao
    }),
    signal: sinal
  });
  return interpretar<RespostaJogadaMotor>(resposta);
}

/** Compat: Leela via rota unificada. */
export async function solicitarJogadaLc0(
  pedido: { fen: string; nivel: NivelClassico; idPartida: string; idPosicao: string },
  sinal: AbortSignal
): Promise<{ jogada: string }> {
  const r = await solicitarJogadaMotor(pedido.fen, pedido.nivel, {
    motor: 'lc0',
    idPartida: pedido.idPartida,
    idPosicao: pedido.idPosicao
  }, sinal);
  return { jogada: r.jogada };
}

export async function consultarEstadoMotor(): Promise<EstadoMotorPublico> {
  const resposta = await fetch('/api/motor/estado');
  const dados = await interpretar<{ motor: EstadoMotorPublico }>(resposta);
  return dados.motor;
}

export async function analisarComMotor(
  fen: string,
  tempoMs = 1200,
  multiPv = 5
): Promise<{
  nomeMotor: string;
  origem: EstadoMotorPublico['origem'];
  duracaoMs: number;
  melhorJogada: string;
  linhas: LinhaAnalisePublica[];
}> {
  const resposta = await fetch('/api/motor/analisar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fen, tempoMs, multiPv })
  });
  return interpretar(resposta);
}

export async function listarCasosBenchessmark(): Promise<CasoBenchessmark[]> {
  const resposta = await fetch('/api/benchessmark/casos', { headers: cabecalhosAdmin() });
  const dados = await interpretar<{ casos: CasoBenchessmark[] }>(resposta);
  return dados.casos;
}

export async function executarItemBenchessmark(
  pedido: { modeloPublicoId: string; casoId: string; modo: ModoAssistenciaModelo },
  sinal: AbortSignal
): Promise<ResultadoItemBenchessmark> {
  const resposta = await fetch('/api/benchessmark/executar-item', {
    method: 'POST',
    headers: cabecalhosAdmin({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(pedido),
    signal: sinal
  });
  const dados = await interpretar<{ resultado: ResultadoItemBenchessmark }>(resposta);
  return dados.resultado;
}

export async function listarProvedores(): Promise<ProvedorAdministrativo[]> {
  const resposta = await fetch('/api/administracao/provedores', { headers: cabecalhosAdmin() });
  const dados = await interpretar<{ provedores: ProvedorAdministrativo[] }>(resposta);
  return dados.provedores;
}

export async function salvarProvedor(
  provedor: Partial<ProvedorEditavel> & { id?: string; chave?: string }
): Promise<ProvedorAdministrativo> {
  const resposta = await fetch(provedor.id ? `/api/administracao/provedores/${provedor.id}` : '/api/administracao/provedores', {
    method: provedor.id ? 'PATCH' : 'POST',
    headers: cabecalhosAdmin({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(provedor)
  });
  const dados = await interpretar<{ provedor: ProvedorAdministrativo }>(resposta);
  return dados.provedor;
}

export async function descobrirModelos(id: string): Promise<ProvedorAdministrativo> {
  const resposta = await fetch(`/api/administracao/provedores/${id}/descobrir`, {
    method: 'POST',
    headers: cabecalhosAdmin()
  });
  const dados = await interpretar<{ provedor: ProvedorAdministrativo }>(resposta);
  return dados.provedor;
}

export async function testarProvedor(id: string): Promise<{ mensagem: string }> {
  const resposta = await fetch(`/api/administracao/provedores/${id}/testar`, {
    method: 'POST',
    headers: cabecalhosAdmin()
  });
  return interpretar<{ mensagem: string }>(resposta);
}

export async function removerProvedor(id: string): Promise<void> {
  const resposta = await fetch(`/api/administracao/provedores/${id}`, {
    method: 'DELETE',
    headers: cabecalhosAdmin()
  });
  await interpretar<{ removido: boolean }>(resposta);
}
